import { useContext } from "react";

import { FormType } from "@simplyk/common";
import { useWatch } from "react-hook-form";

import { scrollToTop } from "../../../../../../../common/helpers/utils";
import { useFormV2Context } from "../../../../contexts/FormV2Context";
import { findStep1FirstErrorPath } from "../../../../helpers/findFirstErrorPath";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useAmplitude } from "@/common/hooks/amplitude/useAmplitude";

export const useAuctionFirstStepSubmit = () => {
  const { logAmplitudeEvent } = useAmplitude();
  const { form, setCurrentStep, formObject: ticketing, setValidationMode, type } = useFormV2Context(FormType.Ticketing);
  const { formAmplitudeContext } = useContext(FrontendFormContext);

  const { trigger, control } = form;

  const logDonorFormStepSubmitted = () => {
    logAmplitudeEvent(AmplitudeEvents.DonorFormStepSubmitted, {
      formId: ticketing.id,
      formType: type,
      formCategory: ticketing.formCategory,
      organizationCountry: ticketing.organization?.country,
      location: "memorable-form.step-1.auction",
      step: "main",
      ...formAmplitudeContext,
    });
  };

  const ticketsPurchased = useWatch({
    control,
    name: "ticketsPurchased",
  });
  const totalBids = ticketsPurchased?.reduce((total, item) => total + (item?.bid || 0), 0) || 0;

  const canSubmitFirstStep = totalBids > 0;

  const focusWithScroll = (element: HTMLElement) => {
    if (element) {
      const elementRect = element.getBoundingClientRect();
      const isElementBelowHalfScreen = elementRect.bottom > window.innerHeight / 2;
      element.focus();
      if (isElementBelowHalfScreen) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }
  };

  const handleErrorFocus = () => {
    const errors = form.formState.errors;
    const firstErrorFieldName = findStep1FirstErrorPath(errors);
    const firstErrorElement = firstErrorFieldName ? document.getElementsByName(firstErrorFieldName)[0] : null;

    if (firstErrorElement) {
      focusWithScroll(firstErrorElement);
    } else {
      form.trigger();
    }
  };

  const onFirstStepSubmit = async () => {
    if (!canSubmitFirstStep) {
      return;
    }

    const isValid = await trigger(["ticketsPurchased", "customAnswersOfParticipants"], {
      shouldFocus: true,
    });

    if (isValid) {
      scrollToTop("auto");
      setCurrentStep("payment");
      logDonorFormStepSubmitted();
      setValidationMode("onBlur");
      form.reset(form.getValues()); // Hide error messages for step 2
    } else {
      handleErrorFocus();
      setValidationMode("onChange");
    }
  };

  return { onFirstStepSubmit, canSubmitFirstStep };
};
